import * as ProRegularSvgIcons from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { useFormikContext } from 'formik'
import React from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import * as Api from 'src/api'
import { useAdminAccess, useApi } from 'src/helpers/hooks'
import { useAuthenticatedHeaders } from 'src/hooks/auth/app'
import { useLocale } from 'src/hooks/locale/locale'
import { useTranslatable } from 'src/hooks/locale/utils'
import Warning from 'src/imgs/classroom_icon.svg'
import { Button } from 'src/tailwind/components/Button'
import { Section } from 'src/tailwind/components/Section'
import * as Table from 'src/tailwind/components/Table'
import { Form } from 'src/views/components/forms/formik/Form'
import { FormError } from 'src/views/components/forms/formik/FormError'
import SelectInput from 'src/views/components/forms/formik/SelectInput'
import TextInput from 'src/views/components/forms/formik/TextInput'
import NoContent from 'src/views/components/NoContent'
import { PaginationBottom, PaginationTop } from 'src/views/components/Pagination'
import { SortButtons } from 'src/views/components/SortButtons'
import SuspenseWrapper from 'src/views/includes/SuspenseWrapper'

const DEFAULT_PAGE = '1'
const DEFAULT_PER_PAGE = '10'

interface FormikValues {
  readonly name: string
  readonly code: string
  readonly facultyId: string
  readonly degree: string
  readonly status: string
}

export default function AdministratorProgramsPage(): JSX.Element {
  const t = useTranslatable()

  return (
    <SuspenseWrapper title={t('program:programs')}>
      <PageContent />
    </SuspenseWrapper>
  )
}

function PageContent(): JSX.Element {
  const t = useTranslatable()
  const locale = useLocale()
  const [searchParams, setSearchParams] = useSearchParams({
    page: DEFAULT_PAGE,
    perPage: DEFAULT_PER_PAGE,
    name: '',
    code: '',
    facultyId: '',
    degree: '',
    status: '1',
    sortBy: '',
  })

  const name = searchParams.get('name') || ''
  const code = searchParams.get('code') || ''
  const facultyId = searchParams.get('facultyId') || ''
  const degree = searchParams.get('degree') || ''
  const status = searchParams.get('status') || ''
  const initialValues: FormikValues = { name, code, facultyId, degree, status }

  const breadcrumbsItems = [
    {
      page: t('program:programs'),
      path: `/${locale}/administrator/administration/programs`,
    },
  ]

  const handleFormSubmit = React.useCallback(
    (values: FormikValues) => {
      setSearchParams((params) => {
        Object.entries(values).forEach(([key, value]) => {
          if (key === 'status') {
            params.set(key, value || '')
          } else if (value) {
            params.set(key, value)
          } else {
            params.delete(key)
          }
        })
        params.set('page', DEFAULT_PAGE)
        return params
      })
    },
    [setSearchParams]
  )
  const navigate = useNavigate()
  const canManagePrograms = useAdminAccess().canManage('programs')
  return (
    <Section
      title={t('program:programs')}
      breadcrubms={breadcrumbsItems}
      icon={<FontAwesomeIcon icon={ProRegularSvgIcons.faList} />}
      rightElement={
        canManagePrograms ? (
          <Button variant="blue" onClick={() => navigate('/administrator/administration/programs/add')}>
            <FontAwesomeIcon icon={ProRegularSvgIcons.faPlus} /> {t('common:add')}
          </Button>
        ) : undefined
      }
    >
      <Form
        initialValues={initialValues}
        onSubmit={handleFormSubmit}
        classNames={{
          form: 'w-full px-2 mt-3 mb-1',
        }}
        enableReinitialize
      >
        <FormSearch />
      </Form>

      <SuspenseWrapper>
        <ProgramsContent searchParams={searchParams} />
      </SuspenseWrapper>
    </Section>
  )
}

function FormSearch(): React.ReactElement | null {
  const formik = useFormikContext<FormikValues>()
  const t = useTranslatable()
  const [searchParams, setSearchParams] = useSearchParams()
  const headers = useAuthenticatedHeaders()

  const { data: faculties } = useApi({
    endpoint: Api.getRegimeFaculties,
    params: React.useMemo(() => ({ headers }), [headers]),
  })

  const { data: degrees } = useApi({
    endpoint: Api.getRegimeDegrees,
    params: React.useMemo(() => ({ headers }), [headers]),
  })

  const { data: definitions } = useApi({
    endpoint: Api.getRegimeDefinitions,
    params: React.useMemo(() => ({ headers }), [headers]),
  })

  const statuses = definitions?.statuses

  const currentName = searchParams.get('name') || ''
  const currentCode = searchParams.get('code') || ''
  const currentFacultyId = searchParams.get('facultyId') || ''
  const currentDegree = searchParams.get('degree') || ''
  const currentStatus = searchParams.get('status') ?? '1'

  const facultyOptions = React.useMemo(
    () => [{ value: '', label: t('common:any') }, ...(faculties ?? []).map((f) => ({ value: f.id, label: f.name }))],
    [faculties, t]
  )

  const degreeOptions = React.useMemo(
    () => [{ value: '', label: t('common:any') }, ...(degrees ?? []).map((d) => ({ value: d.id, label: d.name }))],
    [degrees, t]
  )

  const statusOptions = React.useMemo(
    () => [{ value: '', label: t('common:any') }, ...(statuses ?? []).map((s) => ({ value: s.value, label: s.label }))],
    [statuses, t]
  )

  const isDirty =
    formik.values.name !== currentName ||
    formik.values.code !== currentCode ||
    formik.values.facultyId !== currentFacultyId ||
    formik.values.degree !== currentDegree ||
    formik.values.status !== currentStatus

  const canCancel = !currentName && !currentCode && !currentFacultyId && !currentDegree && currentStatus === '1'

  const handleCancel = React.useCallback(() => {
    const defaultValues = { name: '', code: '', facultyId: '', degree: '', status: '1' }
    setSearchParams({
      ...defaultValues,
      page: DEFAULT_PAGE,
      perPage: DEFAULT_PER_PAGE,
    })
    formik.resetForm({ values: defaultValues })
  }, [setSearchParams, formik])

  return (
    <fieldset disabled={formik.isSubmitting} className="flex w-full flex-col justify-between">
      <FormError />

      <div className="mb-3 flex w-full flex-wrap justify-between">
        <div className="mr-0 w-full min-w-[100px] md:!mb-0 md:mr-2 md:w-[49%]">
          <TextInput type="text" label={t('common:name')} name="name" placeholder={t('common:name')} />
        </div>

        <div className="w-full min-w-[100px] md:!mb-0 md:w-[49%]">
          <TextInput type="text" label={t('common:code')} name="code" placeholder={t('common:code')} />
        </div>

        <div className="mr-0 mt-3 w-full min-w-[100px] md:!mt-3 md:mr-2 md:w-[49%]">
          <SelectInput
            label={t('faculty:faculties')}
            name="facultyId"
            options={facultyOptions}
            disabled={formik.isSubmitting}
          />
        </div>

        <div className="mt-3 w-full min-w-[100px] md:!mt-3 md:w-[49%]">
          <SelectInput
            label={t('person:degree')}
            name="degree"
            options={degreeOptions}
            disabled={formik.isSubmitting}
          />
        </div>

        <div className="mr-0 mt-3 w-full min-w-[100px] md:!mt-3 md:mr-2 md:w-[49%]">
          <SelectInput
            label={t('common:status')}
            name="status"
            options={statusOptions}
            disabled={formik.isSubmitting}
          />
        </div>
      </div>

      <div className="flex w-full justify-end">
        <Button
          variant="red"
          disabled={formik.isSubmitting || !isDirty}
          type="submit"
          onClick={() => formik.submitForm()}
        >
          {t('common:search')}
        </Button>

        <Button
          variant="yellow"
          type="reset"
          disabled={formik.isSubmitting || canCancel}
          className="ml-2"
          onClick={handleCancel}
        >
          {t('common:cancel')}
        </Button>
      </div>
    </fieldset>
  )
}

interface ProgramsContentProps {
  readonly searchParams: URLSearchParams
}

function ProgramsContent({ searchParams }: ProgramsContentProps): JSX.Element | null {
  const headers = useAuthenticatedHeaders()
  const t = useTranslatable()
  const locale = useLocale()

  const name = searchParams.get('name') || ''
  const code = searchParams.get('code') || ''
  const facultyId = searchParams.get('facultyId') || ''
  const degree = searchParams.get('degree') || ''
  const status = searchParams.get('status') || ''
  const page = searchParams.get('page') || DEFAULT_PAGE
  const perPage = searchParams.get('perPage') || DEFAULT_PER_PAGE
  const sortBy = searchParams.get('sortBy') || ''

  const { data: programs } = useApi({
    endpoint: Api.getAdministratorPrograms,
    params: React.useMemo(
      () => ({
        headers,
        query: {
          page: parseInt(page, 10),
          perPage: parseInt(perPage, 10),
          ...(name && { name }),
          ...(code && { code }),
          ...(status && { status: parseInt(status, 10) }),
          ...(facultyId && { facultyId: parseInt(facultyId, 10) }),
          ...(degree && { degree: parseInt(degree, 10) }),
          ...(sortBy && { sortBy }),
        },
      }),
      [headers, page, perPage, name, code, status, facultyId, degree, sortBy]
    ),
  })

  if (!programs?.data || programs.data.length === 0) {
    return <NoContent header={t('error:records_not_found')} image={Warning} marginTop="5" />
  }

  return (
    <div className="mb-7">
      <PaginationTop pagination={programs.meta.pagination} />

      <Table.Table className="mt-7">
        <Table.Thead>
          <TableHeaderRow t={t} />
        </Table.Thead>

        <Table.Tbody>
          {programs.data.map((program) => (
            <TableRow key={program.id} program={program} locale={locale} />
          ))}
        </Table.Tbody>
      </Table.Table>

      <PaginationBottom pagination={programs.meta.pagination} />
    </div>
  )
}

interface TableHeaderRowProps {
  readonly t: ReturnType<typeof useTranslatable>
}

function TableHeaderRow({ t }: TableHeaderRowProps): JSX.Element {
  return (
    <Table.Tr>
      <Table.Th className="!pl-6">
        <div className="flex items-center gap-2">
          <span>{t('common:unique')}</span>
        </div>
      </Table.Th>

      <Table.Th>
        <div className="flex items-center gap-2">
          <span>{t('common:prefix')}</span>
          <SortButtons sortField="prefix" />
        </div>
      </Table.Th>

      <Table.Th>
        <div className="flex items-center gap-2">
          <span>{t('common:code')}</span>
          <SortButtons sortField="code" />
        </div>
      </Table.Th>

      <Table.Th>
        <div className="flex items-center gap-2">
          <span>{t('common:name')}</span>
          <SortButtons sortField="name" />
        </div>
      </Table.Th>

      <Table.Th>
        <div className="flex items-center gap-2">
          <span>{t('person:degree')}</span>
          <SortButtons sortField="degree" />
        </div>
      </Table.Th>

      <Table.Th>
        <span>{t('program:faculty')}</span>
      </Table.Th>
    </Table.Tr>
  )
}

interface TableRowProps {
  readonly program: Api.AdministratorProgram
  readonly locale: string
}

function isPassiveProgram(program: Api.AdministratorProgram): boolean {
  return program.status === 2
}

function TableRow({ program, locale }: TableRowProps): JSX.Element {
  const facultyName = program.faculty?.data?.name ?? '—'
  const isPassive = isPassiveProgram(program)
  const passiveCellClassName = classNames({ '!text-primaryRed': isPassive })

  return (
    <Table.Tr className={classNames({ 'bg-seconderyRed/20': isPassive })}>
      <Table.Td className={classNames('whitespace-nowrap', passiveCellClassName)}>{program.id}</Table.Td>

      <Table.Td className={passiveCellClassName}>{program.prefix}</Table.Td>

      <Table.Td className={passiveCellClassName}>{program.code}</Table.Td>

      <Table.Td className={passiveCellClassName}>
        <Link
          to={`/${locale}/administrator/administration/programs/${program.id}`}
          className="text-primaryBlueLink hover:underline"
        >
          {program.name}
        </Link>
      </Table.Td>

      <Table.Td className={passiveCellClassName}>{program.degree.name}</Table.Td>

      <Table.Td className={passiveCellClassName}>{facultyName}</Table.Td>
    </Table.Tr>
  )
}
